import { MessageCircle, Mail, MapPin, Phone } from "lucide-react";
import { Reveal, SectionHeading } from "../Reveal";
import { waLink, telLink } from "../../lib/api";
import { useContent } from "../../context/ContentContext";

const NumberRow = ({ display, index }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3" data-testid={`contact-number-${index}`}>
    <p className="text-sm font-bold text-navy">{display}</p>
    <div className="mt-2 flex justify-center gap-2">
      <a
        href={telLink(display)}
        data-testid={`contact-call-${index}`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-3.5 py-2 text-xs font-bold text-white transition-transform hover:bg-navy-700 active:scale-95"
      >
        <Phone size={14} /> Call
      </a>
      <a
        href={waLink(display)}
        target="_blank"
        rel="noreferrer"
        data-testid={`contact-whatsapp-${index}`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3.5 py-2 text-xs font-bold text-white transition-transform hover:brightness-95 active:scale-95"
      >
        <MessageCircle size={14} /> WhatsApp
      </a>
    </div>
  </div>
);

export const Contact = () => {
  const { contact } = useContent();
  const numbers = [contact.whatsapp, contact.whatsapp2].filter(Boolean);

  return (
    <section id="contact" className="bg-white py-24" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading center eyebrow={contact.eyebrow} title={contact.title} desc={contact.desc} />
        </Reveal>

        <div className="mx-auto mt-14 grid max-w-4xl items-start gap-6 md:grid-cols-3">
          {/* Phone card: Call or WhatsApp per number */}
          <Reveal>
            <div
              data-testid="contact-card-0"
              className="flex h-full flex-col items-center rounded-2xl border border-gray-200 bg-white p-8 text-center transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#25D366] text-white">
                <Phone size={26} />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-navy">Call or WhatsApp</h3>
              <div className="mt-4 w-full space-y-3">
                {numbers.map((n, i) => (
                  <NumberRow key={i} display={n} index={i + 1} />
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <a
              href={`mailto:${contact.email}`}
              data-testid="contact-card-1"
              className="group flex h-full flex-col items-center rounded-2xl border border-gray-200 bg-white p-8 text-center transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-navy text-white">
                <Mail size={26} />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-navy">Email</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{contact.email}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brandred">Send email</span>
            </a>
          </Reveal>

          <Reveal delay={0.2}>
            <a
              href="/routes"
              data-testid="contact-card-2"
              className="group flex h-full flex-col items-center rounded-2xl border border-gray-200 bg-white p-8 text-center transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-gold text-white">
                <MapPin size={26} />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-navy">Location</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{contact.location}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brandred">View routes</span>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
